import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { STORAGE_KEYS } from '../../config/storageKeys';

const ThemeContext = createContext(null);

// Theme color palettes - each theme has primary, secondary, accent, and highlight colors
export const THEMES = {
  cyberViolet: {
    id: 'cyberViolet',
    name: 'Cyber Violet',
    description: 'Vibrant cyan, violet & fuchsia',
    preview: ['#06b6d4', '#8b5cf6', '#d946ef'],
    colors: {
      primary: '6 182 212',      // cyan-500
      secondary: '139 92 246',   // violet-500
      accent: '217 70 239',      // fuchsia-500
      highlight: '236 72 153',   // pink-500
      gradient: 'from-cyan-500 via-violet-500 to-fuchsia-500',
      gradientText: 'from-cyan-400 via-violet-400 to-fuchsia-400',
      gradientSubtle: 'from-cyan-500/20 via-violet-500/20 to-fuchsia-500/20',
      glow: 'shadow-violet-500/30',
      border: 'border-violet-500/30',
      bg: 'bg-violet-950',
    }
  },
  oceanTeal: {
    id: 'oceanTeal',
    name: 'Ocean Teal',
    description: 'Deep teal & cyan waves',
    preview: ['#0d9488', '#06b6d4', '#0891b2'],
    colors: {
      primary: '13 148 136',     // teal-600
      secondary: '6 182 212',    // cyan-500
      accent: '8 145 178',       // cyan-600
      highlight: '34 211 238',   // cyan-400
      gradient: 'from-teal-600 via-cyan-500 to-sky-500',
      gradientText: 'from-teal-400 via-cyan-400 to-sky-400',
      gradientSubtle: 'from-teal-600/20 via-cyan-500/20 to-sky-500/20',
      glow: 'shadow-cyan-500/30',
      border: 'border-cyan-500/30',
      bg: 'bg-teal-950',
    }
  },
  royalBlue: {
    id: 'royalBlue',
    name: 'Royal Blue',
    description: 'Elegant indigo & sapphire',
    preview: ['#4f46e5', '#6366f1', '#818cf8'],
    colors: {
      primary: '79 70 229',      // indigo-600
      secondary: '99 102 241',   // indigo-500
      accent: '129 140 248',     // indigo-400
      highlight: '165 180 252',  // indigo-300
      gradient: 'from-indigo-600 via-indigo-500 to-violet-500',
      gradientText: 'from-indigo-400 via-indigo-300 to-violet-400',
      gradientSubtle: 'from-indigo-600/20 via-indigo-500/20 to-violet-500/20',
      glow: 'shadow-indigo-500/30',
      border: 'border-indigo-500/30',
      bg: 'bg-indigo-950',
    }
  },
  roseGold: {
    id: 'roseGold',
    name: 'Rose Gold',
    description: 'Soft rose & blush pink',
    preview: ['#be185d', '#db2777', '#ec4899'],
    colors: {
      primary: '190 24 93',      // pink-700
      secondary: '219 39 119',   // pink-600
      accent: '236 72 153',      // pink-500
      highlight: '244 114 182',  // pink-400
      gradient: 'from-pink-700 via-pink-500 to-rose-400',
      gradientText: 'from-pink-400 via-rose-400 to-pink-300',
      gradientSubtle: 'from-pink-700/20 via-pink-500/20 to-rose-400/20',
      glow: 'shadow-pink-500/30',
      border: 'border-pink-500/30',
      bg: 'bg-pink-950',
    }
  },
  slatePro: {
    id: 'slatePro',
    name: 'Slate Pro',
    description: 'Clean, professional & minimal',
    preview: ['#475569', '#64748b', '#94a3b8'],
    colors: {
      primary: '71 85 105',      // slate-600
      secondary: '100 116 139',  // slate-500
      accent: '148 163 184',     // slate-400
      highlight: '226 232 240',  // slate-200
      gradient: 'from-slate-600 via-slate-500 to-slate-400',
      gradientText: 'from-slate-400 via-slate-300 to-gray-300',
      gradientSubtle: 'from-slate-600/20 via-slate-500/20 to-slate-400/20',
      glow: 'shadow-slate-500/20',
      border: 'border-slate-500/30',
      bg: 'bg-slate-900',
    }
  },
  emeraldGreen: {
    id: 'emeraldGreen',
    name: 'Emerald',
    description: 'Fresh green & mint tones',
    preview: ['#059669', '#10b981', '#34d399'],
    colors: {
      primary: '5 150 105',      // emerald-600
      secondary: '16 185 129',   // emerald-500
      accent: '52 211 153',      // emerald-400
      highlight: '110 231 183',  // emerald-300
      gradient: 'from-emerald-600 via-emerald-500 to-teal-400',
      gradientText: 'from-emerald-400 via-emerald-300 to-teal-300',
      gradientSubtle: 'from-emerald-600/20 via-emerald-500/20 to-teal-400/20',
      glow: 'shadow-emerald-500/30',
      border: 'border-emerald-500/30',
      bg: 'bg-emerald-950',
    }
  },
};

export const THEME_MODES = {
  light: 'light',
  dark: 'dark',
  system: 'system',
};

export function ThemeProvider({ children }) {
  // Initialize state from localStorage or defaults
  const [mode, setMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(STORAGE_KEYS.THEME_MODE) || THEME_MODES.dark;
    }
    return THEME_MODES.dark;
  });

  const [colorScheme, setColorScheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(STORAGE_KEYS.THEME_COLOR) || 'cyberViolet';
    }
    return 'cyberViolet';
  });

  // Get actual dark/light state based on mode
  const [isDark, setIsDark] = useState(true);

  // Apply theme colors to CSS variables
  const applyThemeColors = useCallback((themeId) => {
    const theme = THEMES[themeId];
    if (!theme) return;

    const root = document.documentElement;
    root.style.setProperty('--color-primary', theme.colors.primary);
    root.style.setProperty('--color-secondary', theme.colors.secondary);
    root.style.setProperty('--color-accent', theme.colors.accent);
    root.style.setProperty('--color-highlight', theme.colors.highlight);

    // Store the gradient classes as data attributes for JS access
    root.dataset.themeGradient = theme.colors.gradient;
    root.dataset.themeGradientText = theme.colors.gradientText;
    root.dataset.themeGradientSubtle = theme.colors.gradientSubtle;
    root.dataset.themeGlow = theme.colors.glow;
    root.dataset.themeBorder = theme.colors.border;
    root.dataset.themeBg = theme.colors.bg;
  }, []);

  // Apply dark/light mode
  const applyMode = useCallback((modeValue) => {
    let shouldBeDark = true;

    if (modeValue === THEME_MODES.light) {
      shouldBeDark = false;
    } else if (modeValue === THEME_MODES.system) {
      shouldBeDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    } else {
      shouldBeDark = true;
    }

    setIsDark(shouldBeDark);

    if (shouldBeDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  // Effect to apply theme when colorScheme changes
  useEffect(() => {
    applyThemeColors(colorScheme);
    localStorage.setItem(STORAGE_KEYS.THEME_COLOR, colorScheme);
  }, [colorScheme, applyThemeColors]);

  // Effect to apply mode when it changes
  useEffect(() => {
    applyMode(mode);
    localStorage.setItem(STORAGE_KEYS.THEME_MODE, mode);

    // Listen for system preference changes if in system mode
    if (mode === THEME_MODES.system) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => applyMode(THEME_MODES.system);
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [mode, applyMode]);

  // Get the current theme object
  const currentTheme = THEMES[colorScheme] || THEMES.cyberViolet;

  const value = {
    // Current state
    mode,
    colorScheme,
    isDark,
    currentTheme,
    themes: THEMES,
    
    // Setters
    setMode,
    setColorScheme,
    
    // Convenience methods
    toggleMode: () => {
      setMode(prev => prev === THEME_MODES.dark ? THEME_MODES.light : THEME_MODES.dark);
    },
    
    // Get theme-aware class names
    getGradient: () => currentTheme.colors.gradient,
    getGradientText: () => currentTheme.colors.gradientText,
    getGlow: () => currentTheme.colors.glow,
    getBorder: () => currentTheme.colors.border,
    getBg: () => currentTheme.colors.bg,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

// Custom hook to use theme
export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

// Helper hook to get dynamic theme classes
export function useThemeClasses() {
  const { currentTheme } = useTheme();
  return currentTheme.colors;
}

export default ThemeContext;
